import {
  WSAvatar,
  WSButton,
  WSElement,
  WSFlexBox,
  WSList,
  WSSidebar,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  Currency,
  RecurringDocumentStatus
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useState } from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import { Card } from "../../modules/PaymentApp/components/Card/Card";
import { PublicAttachmentDownloadLink } from "../../components/PublicAttachmentDownloadLink/PublicAttachmentDownloadLink";
import { recurringInvoicesApi } from "@api";
import {
  queriesInvoiceRows,
  queriesPayeeRows,
  queriesPayerRows,
  queriesPayers,
  queriesRecurringInvoices
} from "@query";
import { selectorSearchRowName } from "../../shared/selectors/selectorSearchRowName";
import { WSQueries } from "../../query/WSQuery";
import { useWSMutation } from "../../query/helpers";
import { QUERY_INVOICES } from "../../query/payments/keys";
import { useSetWSStore } from "../../store";
import { getLocalStartDayByUTC, getStartOfDay } from "../../utils/dates";
import { InvoiceDetailsHeader } from "../components/InvoiceDetailsHeader/InvoiceDetailsHeader";
import { InvoiceTemplateTotals } from "../components/InvoiceTempalteTotals";
import { SeriesList } from "../components/SeriesList/SeriesList";
import { selectorRecurringInvoiceHeaderState } from "../selectors/selectorRecurringInvoiceHeaderState";
import {
  calculateRecurringInvoiceDueDate,
  calculateRecurringLineItemsTotal
} from "../utils";
import styles from "./InvoicesInvoiceDetails.module.scss";

const RECURRING_INVOICE_DEPENDENCIES = [
  queriesRecurringInvoices.listRecurringInvoices.queryKeyBase,
  queriesRecurringInvoices.getRecurringInvoice.queryKeyBase,
  queriesInvoiceRows.searchInvoiceRows.queryKeyBase,
  QUERY_INVOICES
];

const useMutationActivateRecurringInvoice = () => {
  const history = useHistory();

  return useWSMutation<void, unknown, { recurringInvoiceId: string }>(
    async ({ recurringInvoiceId }) => {
      await recurringInvoicesApi.updateRecurringInvoice({
        recurringInvoiceId,
        recurringInvoiceUpdate: { status: RecurringDocumentStatus.ACTIVE }
      });

      history.push(
        `/member/invoices/template/${recurringInvoiceId}/send-success`
      );
    },
    { dependencies: RECURRING_INVOICE_DEPENDENCIES }
  );
};

const useMutationSendRecurringInvoiceNow = () => {
  const history = useHistory();

  return useWSMutation<
    void,
    unknown,
    { recurringInvoiceId: string; isDraft: boolean }
  >(
    async ({ recurringInvoiceId, isDraft }) => {
      if (isDraft) {
        await recurringInvoicesApi.updateRecurringInvoice({
          recurringInvoiceId,
          recurringInvoiceUpdate: { status: RecurringDocumentStatus.ACTIVE }
        });
      }

      const invoice = await recurringInvoicesApi.generateRecurringInvoiceNow({
        recurringInvoiceId
      });

      history.push(
        invoice.id
          ? `/member/invoices/${invoice.id}/send-success`
          : `/member/invoices/template/${recurringInvoiceId}/send-success`
      );
    },
    { dependencies: RECURRING_INVOICE_DEPENDENCIES }
  );
};

type Props = RouteComponentProps<{
  invoiceTemplateId: string;
  listName?: string;
}>;

export const RouteInvoiceTemplateDetails: React.FC<Props> = ({ match }) => {
  const history = useHistory();
  const location = useLocation<{ backPath?: string }>();

  const onBack = () => {
    const newPath = location.state?.backPath
      ? location.state?.backPath
      : location.pathname.includes("series")
        ? "/member/invoices/scheduled"
        : `/member/invoices${
            match.params.listName ? `/${match.params.listName}` : ""
          }`;

    history.push({
      pathname: newPath,
      search: location.search
    });
  };

  return (
    <WSSidebar onClose={onBack} header={"Invoice # Scheduled"}>
      <Inner
        recurringInvoiceId={match.params.invoiceTemplateId}
        onBack={onBack}
      />
    </WSSidebar>
  );
};

type InnerProps = {
  recurringInvoiceId: string;
  onBack: () => void;
};

const Inner: React.FC<InnerProps> = ({ recurringInvoiceId }) => {
  const history = useHistory();

  const [detailsVisible, setDetailsVisible] = useState(true);
  const [seriesVisible, setSeriesVisible] = useState(false);

  const queryRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery({
      recurringInvoiceId
    });

  const payerId = queryRecurringInvoice.data?.payerId;

  // Both payer reads are decorative and are deliberately kept out of the
  // `WSQueries` gate below: the contact email, the CC list and the client name
  // degrade to blank, where a failure inside the gate would replace the whole
  // drawer with "doesn't exist" (PLA-3583).
  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: payerId! },
    {
      enabled: !!payerId,
      retry: false
    }
  );
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: { id: { eq: payerId! } },
      page: { size: 1 }
    },
    {
      enabled: !!payerId,
      retry: false
    }
  );
  const payer = queryPayer.data;
  const payerRow = queryPayerRow.data?.data?.[0];
  const payerName = payerRow ? selectorSearchRowName(payerRow) : "";

  const [activateRecurringInvoice, activateRecurringInvoiceMeta] =
    useMutationActivateRecurringInvoice();
  const [sendRecurringInvoiceNow, sendRecurringInvoiceNowMeta] =
    useMutationSendRecurringInvoiceNow();
  const location = useLocation();
  const setStore = useSetWSStore();

  return (
    <WSElement className={styles.wrapper}>
      <WSQueries queries={{ queryRecurringInvoice }}>
        {({ queryRecurringInvoice: { data: recurringInvoice } }) => {
          const dueDate = calculateRecurringInvoiceDueDate(recurringInvoice);
          const isDraft =
            recurringInvoice.status === RecurringDocumentStatus.DRAFT;
          const firstSendDate =
            recurringInvoice.schedule?.scheduleDates?.[0]?.date ??
            recurringInvoice.nextOccurrenceDate ??
            recurringInvoice.schedule?.anchorDate;

          const sendDateInFuture =
            !!firstSendDate &&
            getLocalStartDayByUTC(firstSendDate) > getStartOfDay();

          const headerState =
            selectorRecurringInvoiceHeaderState(recurringInvoice);

          const headerButtonProps =
            headerState.kind === "activeSeries"
              ? {
                  children: "Edit series",
                  onClick: () => {
                    setStore({ invoiceFormBackPath: location.pathname });
                    history.push(
                      `/member/invoices/template/${recurringInvoiceId}/edit`
                    );
                  }
                }
              : headerState.kind === "activeOneOff"
                ? {
                    children: "Send now",
                    onClick: () => {
                      sendRecurringInvoiceNow({
                        recurringInvoiceId,
                        isDraft: false
                      });
                    },
                    loading: sendRecurringInvoiceNowMeta.isLoading
                  }
                : headerState.kind === "draft"
                  ? {
                      children: sendDateInFuture ? "Schedule" : "Send now",
                      onClick: () => {
                        if (sendDateInFuture) {
                          activateRecurringInvoice({ recurringInvoiceId });
                        } else {
                          sendRecurringInvoiceNow({
                            recurringInvoiceId,
                            isDraft
                          });
                        }
                      },
                      loading:
                        activateRecurringInvoiceMeta.isLoading ||
                        sendRecurringInvoiceNowMeta.isLoading
                    }
                  : undefined;

          const splits = recurringInvoice.invoiceTemplate?.splits ?? [];

          return (
            <>
              <WSElement className={styles.main}>
                <Card mb="XL">
                  <WSText.Heading4 mb="M">{payerName}</WSText.Heading4>
                  <InvoiceDetailsHeader
                    title={headerState.title}
                    description={headerState.description}
                    buttonProps={headerButtonProps}
                    recurringInvoice={recurringInvoice}
                  />
                </Card>

                <Card mb="XL">
                  <WSFlexBox wrap="nowrap" justify="space-between">
                    <WSButton.Link
                      onClick={() => {
                        setDetailsVisible(!detailsVisible);
                      }}
                      rightIcon={detailsVisible ? "caret-up" : "caret-down"}
                    >
                      {detailsVisible ? "Hide" : "Show"} invoice details
                    </WSButton.Link>
                    <WSText.Heading5>
                      {formatMoney(
                        calculateRecurringLineItemsTotal(
                          recurringInvoice.invoiceTemplate?.lineItems
                        ),
                        recurringInvoice.currency as Currency
                      )}
                    </WSText.Heading5>
                  </WSFlexBox>
                  {detailsVisible && (
                    <WSElement mt="XL">
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Bill to
                        </WSText.ParagraphSm>
                        <WSText>{payerName}</WSText>
                        <WSText>
                          {[
                            // TODO (v3-migration): payer.email is empty for active payers
                            payer?.email,
                            // Invoice CC recipients — now on v3 `Payer.notificationCcEmails` (WIN-15229)
                            ...(payer?.notificationCcEmails || [])
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </WSText>
                      </WSElement>

                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Due date
                        </WSText.ParagraphSm>
                        <WSText>
                          {toWSDateString(dueDate, "monthDayYear")}
                        </WSText>
                      </WSElement>

                      <InvoiceTemplateTotals
                        recurringInvoice={recurringInvoice}
                      />

                      {recurringInvoice.invoiceTemplate
                        ?.customAttachmentIds && (
                        <WSElement mt="XL">
                          {recurringInvoice.invoiceTemplate.customAttachmentIds.map(
                            id => (
                              <PublicAttachmentDownloadLink
                                key={id}
                                id={id}
                                mb="M"
                              />
                            )
                          )}
                        </WSElement>
                      )}

                      {splits.length > 0 && (
                        <WSElement mt="3XL">
                          <WSText.Heading5 mb="M">Contractors</WSText.Heading5>
                          <WSList>
                            {splits.map((split, index) => (
                              <RecurringInvoiceSplitRow
                                key={index}
                                payeeEngagementId={split.payeeEngagementId}
                                description={split.description}
                                amount={split.amount}
                                currency={recurringInvoice.currency as Currency}
                              />
                            ))}
                          </WSList>
                        </WSElement>
                      )}
                    </WSElement>
                  )}
                </Card>

                <Card>
                  <WSButton.Link
                    key={seriesVisible ? "Hide" : "Show"}
                    onClick={() => {
                      setSeriesVisible(v => !v);
                    }}
                    rightIcon={seriesVisible ? "caret-up" : "caret-down"}
                  >
                    {seriesVisible ? "Hide" : "Show"} series details
                  </WSButton.Link>
                  {seriesVisible && (
                    <SeriesList
                      recurringInvoiceId={recurringInvoiceId}
                      mt="XL"
                    />
                  )}
                </Card>
              </WSElement>
            </>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};

type RecurringInvoiceSplitRowProps = {
  payeeEngagementId?: string;
  description?: string;
  amount?: number;
  currency: Currency;
};

const RecurringInvoiceSplitRow: React.FC<
  RecurringInvoiceSplitRowProps
> = props => {
  const queryPayeeRow = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        engagements_id: { eq: props.payeeEngagementId! }
      },
      page: { size: 1 }
    },
    { enabled: !!props.payeeEngagementId, retry: false }
  );

  const payeeRow = queryPayeeRow.data?.data?.[0];

  if (!payeeRow) {
    return null;
  }

  const payeeName = selectorSearchRowName(payeeRow);

  return (
    <WSFlexBox.CenterY wrap="nowrap">
      <WSAvatar.Text text={payeeName} mr="M" />
      <WSElement style={{ flex: 1 }}>
        <WSText mb="XS">{payeeName}</WSText>
        <WSText.ParagraphXs color="gray600">
          {props.description}
        </WSText.ParagraphXs>
      </WSElement>
      <WSText>{formatMoney(props.amount ?? 0, props.currency)}</WSText>
    </WSFlexBox.CenterY>
  );
};
